import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { 
    useGetAllInternalAccounts, 
    useCreateInternalAccount, 
    useUpdateInternalAccount,
    useCheckInternalAccountExists,
    getCheckInternalAccountExistsQueryKey,
    checkInternalAccountExists
} from '@/shared/api/client/internal-account-resource';
import { useGetAllCompanies } from '@/shared/api/client/company-resource';
import { InternalAccountDTO, useInternalAccountStore } from '../stores/internal-account-store';
import {CheckInternalAccountExistsParams, GetAllInternalAccountsParams} from "@/shared/api/model";

export const useInternalAccounts = () => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [branchFilter, setBranchFilter] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    
    // Get state from store
    const { 
        accounts,
        filteredAccounts,
        searchTerm,
        isLoading: storeLoading,
        
        setAccounts,
        setSearchTerm,
        setIsLoading
    } = useInternalAccountStore();

    // Create account mutation
    const createAccountMutation = useCreateInternalAccount({
        mutation: {
            onSuccess: () => {
                message.success('Internal account created successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/internal-accounts'] });
            },
            onError: (error:any) => {
                console.error('Error creating internal account:', error);
                message.error('Failed to create internal account');
                setError('Failed to create internal account');
            }
        }
    });

    // Update account mutation
    const updateAccountMutation = useUpdateInternalAccount({
        mutation: {
            onSuccess: () => {
                message.success('Internal account updated successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/internal-accounts'] });
            },
            onError: (error) => {
                console.error('Error updating internal account:', error);
                message.error('Failed to update internal account');
                setError('Failed to update internal account');
            }
        }
    });

    // Get all branches for filtering
    // @ts-ignore
    const branchesQuery = useGetAllCompanies();
    const branches = branchesQuery.data || [];

    // Get all internal accounts with filters
    const params: GetAllInternalAccountsParams = {};
    if (branchFilter) params.branch = branchFilter;
    if (statusFilter) params.acStatus = statusFilter;

    const internalAccountsQuery = useGetAllInternalAccounts(params);
    
    // Update accounts in store when data changes
    useEffect(() => {
        if (internalAccountsQuery.data) {
            // @ts-ignore
            setAccounts(internalAccountsQuery.data);
        }
    }, [internalAccountsQuery.data, setAccounts]);

    // Check if account exists
    // Note: We don't call this directly here, but provide a function that uses it
    // The function will be called with specific parameters when needed

    // Create a new internal account
    const createAccount = (account: InternalAccountDTO) => {
        createAccountMutation.mutate({
            data: account
        });
    };

    // Update an existing internal account
    const updateAccount = (account: InternalAccountDTO) => {
        updateAccountMutation.mutate({
            data: account
        });
    };

    // Close an internal account by updating its status
    const closeAccount = (account: InternalAccountDTO) => {
        const updatedAccount = { ...account, acStatus: 'C' };
        updateAccountMutation.mutate({
            data: updatedAccount
        });
    };

    // Check if an account exists
    const checkAccountExists = async (branch: string, glNo: string, acNo: string): Promise<boolean> => {
        try {
            const params: CheckInternalAccountExistsParams = {
                branch,
                glNo,
                acNo
            };
            
            const response = await queryClient.fetchQuery({
                queryKey: getCheckInternalAccountExistsQueryKey(params),
                queryFn: () => checkInternalAccountExists(params)
            });
            
            return response;
        } catch (error) {
            console.error('Error checking account existence:', error);
            return false;
        }
    };

    // Filter by branch
    const filterByBranch = (branch: string | undefined) => {
        setBranchFilter(branch);
    };

    // Filter by status
    const filterByStatus = (status: string | undefined) => {
        setStatusFilter(status);
    };

    // Reset all filters
    const resetFilters = () => {
        setBranchFilter(undefined);
        setStatusFilter(undefined);
        setSearchTerm('');
    };

    // Refresh accounts data
    const loadAccounts = () => {
        queryClient.invalidateQueries({ queryKey: ['/api/internal-accounts'] });
    };

    return {
        // Data
        accounts,
        filteredAccounts,
        searchTerm,
        branches,
        branchFilter,
        statusFilter,
        error,
        isLoading: internalAccountsQuery.isLoading || branchesQuery.isLoading || storeLoading,
        isCreating: createAccountMutation.isPending,
        isUpdating: updateAccountMutation.isPending,
        
        // Actions
        createAccount,
        updateAccount,
        closeAccount,
        checkAccountExists,
        loadAccounts,
        setSearchTerm,
        filterByBranch,
        filterByStatus,
        resetFilters
    };
};
